import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { SnapshotSelector } from "./SnapshotSelector";
import { UploadZone } from "./UploadZone";
import { ProductionDrivers } from "./ProductionDrivers";
import { listUsers, createUser, resetUserPassword, deleteUser, changeMyPassword, getAllBuyers } from "@/lib/procureApi";
import { fmtInt } from "@/lib/format";
import { GitDiff, SignOut, UserCircle, FileXls, UsersThree, Trash, Key, Plus, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";

const Section = ({ label, icon: Icon, children }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-1.5 pf-label">{Icon && <Icon size={13} weight="bold" />}{label}</div>
    {children}
  </div>
);

const inputCls = "h-9 w-full rounded-sm border border-[#E3E7EC] bg-[#F9FAFB] px-2.5 text-sm text-[#0F172A] outline-none focus:border-[#F0323E] focus:bg-white";

const ChangePassword = () => {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!cur || !nw) return;
    setBusy(true);
    try { await changeMyPassword(cur, nw); toast.success("Password updated"); setCur(""); setNw(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update password"); }
    finally { setBusy(false); }
  };
  return (
    <Section label="Change my password" icon={LockKey}>
      <input data-testid="cp-current" type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Current password" className={inputCls} />
      <input data-testid="cp-new" type="password" value={nw} onChange={(e) => setNw(e.target.value)} placeholder="New password" className={inputCls} />
      <button data-testid="cp-submit" onClick={submit} disabled={busy} className="w-full rounded-sm bg-[#0F172A] px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1e293b] disabled:opacity-50">Update password</button>
    </Section>
  );
};

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [form, setForm] = useState({ username: "", password: "", role: "read_only", buyer: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [u, b] = await Promise.all([listUsers(), getAllBuyers()]);
      setUsers(u.users || []);
      setBuyers(b.buyers || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.username || !form.password) { toast.error("Username and password required"); return; }
    if (form.role === "buyer" && !form.buyer) { toast.error("Pick which buyer this account maps to"); return; }
    setBusy(true);
    try {
      await createUser(form);
      toast.success(`Account "${form.username}" created`);
      setForm({ username: "", password: "", role: "read_only", buyer: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create account"); }
    finally { setBusy(false); }
  };

  const doReset = async (username) => {
    const pw = window.prompt(`New password for "${username}"`);
    if (!pw) return;
    try { await resetUserPassword(username, pw); toast.success(`Password reset for ${username}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not reset"); }
  };

  const doDelete = async (username) => {
    if (!window.confirm(`Delete account "${username}"?`)) return;
    try { await deleteUser(username); toast.success(`Deleted ${username}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const roleLabel = { admin: "Admin", read_only: "Read only", buyer: "Buyer" };

  return (
    <Section label="Buyer & user accounts" icon={UsersThree}>
      <div className="space-y-2 rounded-sm border border-[#E3E7EC] bg-[#F9FAFB] p-3" data-testid="create-user-form">
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="nu-username" value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} placeholder="Username" className={inputCls} />
          <input data-testid="nu-password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Password" className={inputCls} />
        </div>
        <select data-testid="nu-role" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className={inputCls}>
          <option value="read_only">Read only — view full team</option>
          <option value="admin">Admin — view full team + upload</option>
          <option value="buyer">Buyer — view only their own data</option>
        </select>
        {form.role === "buyer" && (
          <select data-testid="nu-buyer" value={form.buyer} onChange={(e) => setForm((f) => ({ ...f, buyer: e.target.value }))} className={inputCls}>
            <option value="">Select the buyer this account maps to…</option>
            {buyers.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        )}
        <button data-testid="nu-create" onClick={create} disabled={busy} className="flex w-full items-center justify-center gap-1.5 rounded-sm bg-[#F0323E] px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#d92834] disabled:opacity-50">
          <Plus size={14} weight="bold" /> Create account
        </button>
      </div>

      <div className="divide-y divide-[#E3E7EC] rounded-sm border border-[#E3E7EC]" data-testid="user-list">
        {users.map((u) => (
          <div key={u.username} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`user-row-${u.username}`}>
            <div className="min-w-0">
              <div className="truncate font-mono text-xs text-[#0F172A]">{u.username}</div>
              <div className="pf-label">{roleLabel[u.role] || u.role}{u.role === "buyer" && u.buyer ? ` · ${u.buyer}` : ""}</div>
            </div>
            <div className="flex shrink-0 gap-1">
              <button data-testid={`user-reset-${u.username}`} onClick={() => doReset(u.username)} title="Reset password" className="flex h-7 w-7 items-center justify-center rounded-sm border border-[#E3E7EC] text-[#64748B] hover:bg-[#EDEFF3] hover:text-[#0F172A]"><Key size={13} weight="bold" /></button>
              <button data-testid={`user-delete-${u.username}`} onClick={() => doDelete(u.username)} title="Delete" className="flex h-7 w-7 items-center justify-center rounded-sm border border-[#E3E7EC] text-[#64748B] hover:bg-[#FEE2E2] hover:text-[#F0323E]"><Trash size={13} weight="bold" /></button>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

export const SettingsPanel = ({
  open, onClose, loaded, snapshots, snapshotId, setSnapshotId,
  onOpenCompare, currentSnap, meta, isAdmin, isBuyer, onUploaded, user, signOut, onDriversChanged,
}) => (
  <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
    <SheetContent side="right" data-testid="settings-panel" className="w-full overflow-y-auto border-l border-[#E3E7EC] bg-white text-[#0F172A] sm:max-w-md">
      <SheetHeader className="text-left">
        <SheetTitle className="text-lg">Settings</SheetTitle>
        <SheetDescription className="text-[#64748B]">Account, snapshots, uploads and user management.</SheetDescription>
      </SheetHeader>

      <div className="mt-6 space-y-6">
        <Section label="Signed in as" icon={UserCircle}>
          <div className="flex items-center justify-between rounded-sm border border-[#E3E7EC] bg-[#F9FAFB] px-3 py-2.5">
            <div className="flex items-center gap-2">
              <UserCircle size={22} weight="duotone" className="text-[#F0323E]" />
              <div>
                <div className="font-mono text-sm text-[#0F172A]" data-testid="current-user">{user?.username}</div>
                <div className="pf-label">{user?.role === "admin" ? "Admin" : user?.role === "buyer" ? `Buyer · ${user?.buyer || ""}` : "Read only"}</div>
              </div>
            </div>
            <button data-testid="logout-button" onClick={signOut} className="flex items-center gap-1.5 rounded-sm border border-[#E3E7EC] px-2.5 py-1.5 text-xs font-medium text-[#64748B] transition-colors hover:bg-[#EDEFF3] hover:text-[#0F172A]">
              <SignOut size={14} weight="bold" /> Sign out
            </button>
          </div>
        </Section>

        <ChangePassword />

        {loaded && (
          <Section label="Snapshot" icon={FileXls}>
            <SnapshotSelector snapshots={snapshots} value={snapshotId} onChange={setSnapshotId} />
            {!isBuyer && (
              <button
                data-testid="open-compare"
                onClick={() => { onClose(); onOpenCompare(); }}
                disabled={snapshots.length < 2}
                title={snapshots.length < 2 ? "Upload another day's report to compare" : "Compare two snapshots"}
                className="mt-1 flex w-full items-center justify-center gap-2 rounded-sm border border-[#CAD1DB] bg-white px-3 py-2 text-sm font-medium text-[#0F172A] transition-colors hover:bg-[#EDEFF3] disabled:opacity-40"
              >
                <GitDiff size={16} weight="bold" /> Compare snapshots
              </button>
            )}
          </Section>
        )}

        {!isBuyer && (currentSnap || meta?.po) && (
          <Section label="Loaded files" icon={FileXls}>
            <div className="space-y-2">
              {currentSnap && (
                <div className="flex items-center gap-2 rounded-sm border border-[#E3E7EC] bg-[#F9FAFB] px-3 py-2">
                  <FileXls size={18} weight="duotone" className="text-[#F0323E]" />
                  <div className="min-w-0">
                    <div className="truncate font-mono text-xs text-[#0F172A]">{currentSnap.filename}</div>
                    <div className="pf-label">{fmtInt(currentSnap.row_count)} lines</div>
                  </div>
                </div>
              )}
              {meta?.po && (
                <div className="flex items-center gap-2 rounded-sm border border-[#E3E7EC] bg-[#F9FAFB] px-3 py-2" data-testid="po-badge" title={meta.po.filename}>
                  <FileXls size={18} weight="duotone" className="text-[#8B5CF6]" />
                  <div className="min-w-0">
                    <div className="truncate font-mono text-xs text-[#0F172A]">PO Query · {meta.po.reference_date || ""}</div>
                    <div className="pf-label">{fmtInt(meta.po.row_count)} rows</div>
                  </div>
                </div>
              )}
            </div>
          </Section>
        )}

        {isAdmin && (
          <Section label="Upload daily report" icon={FileXls}>
            <UploadZone compact onUploaded={(res) => onUploaded(res)} />
          </Section>
        )}

        <ProductionDrivers isAdmin={isAdmin} onChanged={onDriversChanged} />

        {isAdmin && <UserManager />}
      </div>
    </SheetContent>
  </Sheet>
);
